import React from 'react';
import {
  NavLink,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import titleize from "underscore.string/titleize";
import humanize from "underscore.string/humanize";
import endsWith from "underscore.string/endsWith";
import strLeft from "underscore.string/strLeft";
import {
  Layouts,
  Buttons,
  AttributeValue,
  ResponsiveTable,
  Loaders,
} from "./../components";
import { formatMoney } from "./../utils/currrency";
import {
  StatusIndicator,
  SubscribersList,
} from "./../components/preoffer";
import {
  usePreoffer,
  usePreofferPitch,
} from "../queries/preoffer";
import {formatDate}  from "../utils/datetime";
import { useSessionContext } from "./../contexts/SessionContext";
import { OFFER_STATUSES } from "../config/constants";

function Overview() {
  const navigate = useNavigate();
  const location = useLocation();
  let { preoffer_id } = useParams();
  const {data:preoffer, isLoading } = usePreoffer({preoffer_id});
  const {data:pitch, isLoading:isLoadingPitch} = usePreofferPitch({preoffer_id});
  const { isPermitted, ACTIONS } = useSessionContext();

  const filledPitchSections = []

  for (const [key, value] of Object.entries(pitch)) {
    if(endsWith(key, "_section") && value != null){
      filledPitchSections.push({name: strLeft(key, "_section"), section: value})
    }
    else {
      continue;
    }
  }

  const columns = [
    {
      Header: 'Section name',
      accessor: 'name',
      Cell: ({value}) => value ? humanize(value) : null
    },{
      Header: 'Last updated',
      accessor: 'section.updated_at',
      Cell: ({value}) => value ? formatDate(value, 'MMM dd, yyyy, hh:mm a') : null
    },
  ];

  const openEditorModal = React.useCallback(
    (row) => {
      navigate(
        "edit",
        {
          state: {
            backgroundLocation: location
          }
        }
      )
    },
    [navigate, location]
  )

  if(isLoading || isLoadingPitch)
    return <Loaders.DashboardLoader />

  return (
    <>
      <Layouts.PageHeader
        left={titleize(preoffer.name)}
        right={<StatusIndicator preoffer={preoffer} />}
      />
      <div className="overview-content">
        <div className="nav-tabs-container">
          <ul className="nav nav-tabs px-3" role="tablist">
            <li className="nav-item">
              <NavLink to="preoffer-overview" className="nav-link active" data-bs-toggle="tab" data-bs-target="#offer-overview" role="tab">Overview</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="offer-investors" className="nav-link" data-bs-toggle="tab" data-bs-target="#offer-investors" role="tab">Subscribers</NavLink>
            </li>
          </ul>
        </div>
        <div className="tab-content px-3">
          <div className="tab-pane fade show active" id="offer-overview" role="tabpanel">
            <Layouts.OverviewSubheader left='Pre-launch Overview' tooltipDesc="All the core details about your pre-launch. Spaced Ventures admins set this up and it is only viewable by Issuers." />
            <Layouts.TwoColumnLayout
              left={<AttributeValue name='Offer name' value={titleize(preoffer.name)} />}
              right={<AttributeValue name='Company name' value={titleize(preoffer.company.name)} />}
            />

            <Layouts.TwoColumnLayout
              left={
                <>
                  <AttributeValue name='Offer goal' value={formatMoney(preoffer.goal_amount)} />
                </>
              }
            />

            <Layouts.OverviewSubheader
              left='Pitch Sections'
              right={
                preoffer.status !== OFFER_STATUSES.LIVE && isPermitted(ACTIONS.WRITE_UPDATE_PITCH, preoffer.company.id) ? <Buttons.TextButton title='Edit' onClick={openEditorModal} /> : null
              }
              tooltipDesc="All the pitch sections for a pre-launch will be listed below and when they were last updated."
            />
            <ResponsiveTable title="Pitch Sections" columns={columns} data={filledPitchSections}/>
          </div>

          <div className="tab-pane fade show" id="offer-investors" role="tabpanel">
            <Layouts.OverviewSubheader left='Performance' tooltipDesc="The performance of your pre-launch. Includes the total amount raised and the number of unique investors." />
            <div className="two-col-layout">
              <div className="first-col p-3">
                <AttributeValue
                  name={
                    <>
                      <span>Raised:</span>
                      <span style={{fontWeight: 'bold', marginLeft: '5px'}}>{
                        `${formatMoney(preoffer.committed || 0, {precision : 0})} / ${formatMoney(preoffer.goal_amount || 0, {precision : 0})}`
                        }</span>
                    </>
                  }
                  value={
                    <div style={{ width: '300px', backgroundColor: '#fff', flexDirection: 'row'}}>
                      <div style={{width: `${Math.min(Math.max(preoffer.committed*100 / preoffer.goal_amount, .5), 100) || 0}%`, height: '18px', backgroundColor: '#5AB4AD'}} />
                    </div>
                  }
                />
              </div>
              <div className="second-col p-3">
                <AttributeValue name='Number of subscribers' value={preoffer.preoffer_subscribers_count} />
              </div>
            </div>
            <SubscribersList preofferId={preoffer.slug} downloadEnabled={isPermitted(ACTIONS.READ_DOWNLOAD_PREOFFER_SUBSCRIBERS, preoffer.company.id)} />
          </div>
        </div>
      </div>
    </>
  )
}

Overview.defaultProps = {};

Overview.propTypes = {};

export default Overview;